'use client';

import { cn } from '@coas/ui/lib';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useId, useMemo, useState } from 'react';
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form';
import IconEyeClosed from '~/shared/assets/icons/eye-closed.svg';
import IconEyeOpen from '~/shared/assets/icons/eye-open.svg';
import type { PropsOf } from '~/shared/lib';

type AuthInputProps<TFieldValues extends FieldValues = never> = PropsOf<'input'> &
    UseControllerProps<TFieldValues> & {
        label?: React.ReactNode;
    };

export default <TFieldValues extends FieldValues = never>({ label, ...props }: AuthInputProps<TFieldValues>) => {
    const { field, fieldState } = useController(props);

    const id = useId();
    const [parent] = useAutoAnimate();

    const isPassword = useMemo(() => props.type === 'password', [props.type]);
    const [type, setType] = useState(props.type);

    return (
        <div className={'space-y-1'}>
            {!!label && (
                <label htmlFor={id} className={'font-500 text-neutral-500'}>
                    {label}
                </label>
            )}
            <div ref={parent} className={'flex flex-col items-stretch'}>
                <div className={'relative'}>
                    <input
                        id={id}
                        {...props}
                        {...field}
                        type={type}
                        className={cn(
                            'w-full border-2 border-neutral-300 bg-transparent p-2 outline-none outline-2 focus:border-blue-600',
                            isPassword && 'pr-10',
                            fieldState.invalid && 'border-red-600',
                            props.className,
                        )}
                    />
                    {isPassword && (
                        <button
                            type={'button'}
                            className={
                                'absolute inset-y-0 right-2 my-auto h-fit rounded-2 outline-2 outline-neutral-400 focus-visible:outline'
                            }
                            onClick={() => setType(prev => (prev === 'password' ? 'text' : 'password'))}
                        >
                            {type === 'password' && <IconEyeOpen className={'h-5 w-5 stroke-neutral-400'} />}
                            {type === 'text' && <IconEyeClosed className={'h-5 w-5 stroke-neutral-400'} />}
                        </button>
                    )}
                </div>
                {fieldState.invalid && !!fieldState.error?.message && (
                    <span className={'bg-red-200 p-2 font-400 text-14 text-red-500'}>{fieldState.error.message}</span>
                )}
            </div>
        </div>
    );
};
